import { makeObservable, observable, action, flow } from "mobx";
import { drivesData } from "../../../pages/v2/DrivesPage/mockData";
import { createDrive, listDrives } from "../../../services/drives";
import { getVaultFolder } from "../../../services/vault";
import { mockVaultFolderResponse } from "../../../pages/v2/DrivesPage/mockDataV2";


class DrivesStore {
  drives = [];
  isLoading = false;
  error = null;

  // Paginación backend para listado de drives
  currentPage = 1;
  perPage = 10;
  hasMore = false;
  isLoadingMore = false;
  meta = null;

  isCreatingDrive = false;
  createdDrive = false;
  driveAlreadyExists = false;
  isErrorCreateDrive = false;

  constructor() {
    makeObservable(this, {
      // Observables
      drives: observable,
      isLoading: observable,
      error: observable,
      currentPage: observable,
      perPage: observable,
      hasMore: observable,
      isLoadingMore: observable,
      meta: observable,
      isCreatingDrive: observable,
      createdDrive: observable,
      driveAlreadyExists: observable,
      isErrorCreateDrive: observable,

      // Actions
      setDrives: action,
      setLoading: action,
      setError: action,
      setCreatedDrive: action,
      setDriveAlreadyExists: action,
      setIsErrorCreateDrive: action,
      renameDrive: action,

      // Flows (async actions)
      fetchDrives: flow,
      getDriveById: flow,
      createDrive: flow,
      deleteDrive: flow,
      getVaultFolderData: flow
    });
  }

  // Actions
  
  setDrives = (drives) => {
    this.drives = drives;
  };

  setLoading = (status) => {
    this.isLoading = status;
  };

  setError = (error) => {
    this.error = error;
  };
  
  setCreatedDrive = (status) => {
    this.createdDrive = status;
    if (!status) {
      this.driveCreated = null;
    }
  };
  
  setDriveAlreadyExists = (status) => {
    this.driveAlreadyExists = status;
  };
  
  setIsErrorCreateDrive = (status) => {
    this.isErrorCreateDrive = status;
  };

  renameDrive = (driveId, newTitle) => {
    this.drives = this.drives.map((drive) =>
      drive.id === driveId ? { ...drive, title: newTitle } : drive
    );
  };

  // API Actions
  *fetchDrives(searchText = "", page = 1, append = false) {
    const isInitialLoad = page === 1 && !append;

    try {
      if (isInitialLoad) {
        this.setLoading(true);
      } else {
        this.isLoadingMore = true;
      }

      const baseMeta = {
        page,
        page_size: this.perPage,
        order_by: "created_at",
        order_direction: "DESC",
        ...(searchText && {
          search: {
            field: "name",
            keyword: searchText,
          },
        }),
        filters: {},
      };

      const resp = yield listDrives({ scope: "MANAGE", meta: baseMeta });
      const d = resp?.data;

      if (!d) {
        console.warn("⚠️ MANAGE: No data found in response");
        if (!append) {
          this.setDrives([]);
        }
        this.hasMore = false;
        this.meta = null;
        this.currentPage = page;
        return;
      }

      let items = [];

      if (Array.isArray(d.body)) items = d.body;
      else if (Array.isArray(d.data)) items = d.data;
      else if (Array.isArray(d?.body?.data)) items = d.body.data;
      else if (Array.isArray(d.items)) items = d.items;
      else {
        console.warn("⚠️ MANAGE: No valid array found in response. Keys:", Object.keys(d));
        items = [];
      }

      const mapped = items.map((item) => ({
        id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        title: item.name || item.title || "Untitled Drive",
        lastUpdated:
          item.updatedAt || item.updated_at || item.latest_updated || new Date().toISOString(),
        lastUpdatedTooltip: undefined,
        folderCount:
          typeof item.folderCount !== "undefined"
            ? `${item.folderCount} folders`
            : typeof item.folders_count !== "undefined"
            ? `${item.folders_count} folders`
            : item.folderCount || undefined,
        scope: item.scope || item.category || "MANAGE",
      }));

      if (append) {
        const existingIds = new Set(this.drives.map((drive) => drive.id));
        const merged = [...this.drives];

        mapped.forEach((drive) => {
          if (!existingIds.has(drive.id)) {
            existingIds.add(drive.id);
            merged.push(drive);
          }
        });

        this.setDrives(merged);
      } else {
        this.setDrives(mapped);
      }

      const meta = d.meta || {};
      const hasNext =
        typeof meta.has_next !== "undefined"
          ? meta.has_next
          : typeof meta.hasNext !== "undefined"
          ? meta.hasNext
          : typeof meta.hasMore !== "undefined"
          ? meta.hasMore
          : false;

      this.meta = meta;
      this.hasMore = !!hasNext;
      this.currentPage = page;
    } catch (error) {
      console.warn("Error fetching drives from API, falling back to mock data:", error);
      if (!append) {
        this.setDrives(drivesData);
      }
      this.hasMore = false;
    } finally {
      if (isInitialLoad) {
        this.setLoading(false);
      } else {
        this.isLoadingMore = false;
      }
    }
  }

  *getDriveById(id) {
    // TODO: Implement call to service
  }

  

  *createDrive(driveData) {
    try {
      this.isCreatingDrive = true;
      this.driveAlreadyExists = false;
      this.isErrorCreateDrive = false;
      
      const response = yield createDrive(driveData);
      const vault = response.data.body;
      this.createdDrive = true;
      this.driveCreated = vault;
      return this.driveCreated;
    } catch (error) {
      this.isErrorCreateDrive = true;
      if (error.response && error.response.data) {
        const { status_code, msg_code } = error.response.data;
        if (status_code === 409 && msg_code && msg_code.some(msg => msg.code === "VLT_USER_SCOPE_EXISTS")) {
          this.isErrorCreateDrive = false;
          this.driveAlreadyExists = true;
        }
      }
      return null;
    } finally {
      this.isCreatingDrive = false;
    }
  }

  *deleteDrive(id) {
    // TODO: Implement call to service
     
  }

  *getVaultFolderData(vaultUuid, folderUuid = null, foldersParams = {}, documentsParams = {}) {
    console.log("getVaultFolderData", vaultUuid, folderUuid, foldersParams, documentsParams);
    try {
      const defaultParams = {
        limit: 20,
        offset: 0,
        order_by: "updated_at",
        order_direction: "ASC"
      };

      const response = yield getVaultFolder({
        vault_uuid: vaultUuid,
        folder_uuid: folderUuid,
        folders: { ...defaultParams, ...foldersParams },
        documents: { ...defaultParams, ...documentsParams }
      });

      return response.data;
    } catch (error) {
      console.warn('Error fetching vault folder data, using mock data:', error);
      return mockVaultFolderResponse;
    }
  }
}

export default DrivesStore;